import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, Users, ScrollText, MessageSquareWarning, Network, Bot, Radio, Lock, KeyRound, ArrowRight } from "lucide-react";
import Seo, { breadcrumbLd } from "@/components/Seo";
import { MarketingNav, MarketingFooter } from "@/components/MarketingChrome";

const DOMAINS = [
  {
    icon: Lock,
    title: "Hard multi-tenant isolation",
    body: "Every community's conversations, documents and records live behind strict tenant boundaries. A person only ever reaches the communities they belong to, enforced in the backend on every request, not just hidden in the interface.",
  },
  {
    icon: Users,
    title: "Role-based access & permissions",
    body: "A clear hierarchy, resident, board member, community manager, company admin and platform staff, plus custom roles you define. Enabled modules and approvals decide exactly what each person can see and do. The manager Copilot is scoped to the selected community, never a free-roaming query engine.",
  },
  {
    icon: ScrollText,
    title: "Audit logging",
    body: "Sensitive actions are recorded with who, what and when. Managers and admins can review the activity log, so there's always an accountable trail behind a change, an approval or a message that went out.",
  },
  {
    icon: MessageSquareWarning,
    title: "Messaging compliance",
    body: "TCPA-aware consent capture, A2P 10DLC brand and campaign registration for text, honored opt-outs (STOP/HELP) and quiet hours. Residents opt in before they're messaged, and every broadcast respects that consent.",
  },
  {
    icon: Network,
    title: "Safe outbound fetching",
    body: "When Redline reads a community's website or a monitored page, requests are guarded against SSRF and DNS-rebinding, with bounded reads. Site traffic analytics are paginated and searchable without ever exposing raw IP or user-agent data.",
  },
  {
    icon: Radio,
    title: "Reliable background operations",
    body: "Emergency paging and scheduled loops run isolated from live web traffic, with worker supervision, a leader lease and atomic paging claims. The concierge answering residents can't be starved by a background job, and vice-versa.",
  },
  {
    icon: Bot,
    title: "AI safety & grounding",
    body: "Answers are grounded in each community's own documents, rules and contacts. When something needs a person, the concierge hands off to a human. Copilot actions are confirmed and community-scoped, nothing acts on your behalf without an owner and an approval.",
  },
  {
    icon: KeyRound,
    title: "Encryption & least-privilege access",
    body: "Traffic is encrypted in transit, and internal access follows least-privilege, people and systems get only the access the task in front of them requires, and nothing more. Your community's data stays yours; it's never sold or repurposed for advertising.",
  },
];

export default function SecurityPage() {
  const jsonLd = breadcrumbLd([{ name: "Home", path: "/" }, { name: "Security", path: "/security" }]);
  return (
    <div className="font-body bg-sand-50 text-ink min-h-screen" data-testid="security-page">
      <Seo
        title="Security & Data Protection, Ask Redline"
        description="How Ask Redline protects community data: hard multi-tenant isolation, role-based access, audit logging, TCPA/A2P 10DLC messaging compliance, safe outbound fetching and AI grounding."
        canonical="/security"
        jsonLd={jsonLd}
        scrollTop
      />
      <MarketingNav />

      <section className="max-w-6xl mx-auto px-6 pt-14 pb-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-redline-50 text-redline-700 text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 mb-5">
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2} /> Security
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08]">Security your management company can stand behind.</h1>
          <p className="mt-5 text-lg text-ink-700 leading-relaxed">
            You're trusting Ask Redline with residents' questions, your governing documents and the day-to-day operation of every
            community you run. Here's how that data is kept separated, access is controlled, and every action stays accountable.
          </p>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16 grid md:grid-cols-2 gap-5" data-testid="security-domains">
        {DOMAINS.map((d, i) => {
          const Icon = d.icon;
          return (
            <motion.div key={d.title} data-testid={`security-domain-${i}`}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: (i % 2) * 0.06 }}
              className="flex flex-col h-full rounded-3xl border border-sand-200 bg-white p-8">
              <div className="h-11 w-11 rounded-xl bg-redline-50 flex items-center justify-center shrink-0 mb-5">
                <Icon className="h-5 w-5 text-redline-600" strokeWidth={1.75} />
              </div>
              <h2 className="font-heading text-xl font-extrabold tracking-tight">{d.title}</h2>
              <p className="text-sm text-ink-600 mt-3 leading-relaxed">{d.body}</p>
            </motion.div>
          );
        })}
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-ink text-white p-10 sm:p-14">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-redline-500/20 blur-3xl" />
          <div className="relative max-w-2xl">
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Want the details for your review?</h2>
            <p className="mt-4 text-sand-300 leading-relaxed">
              We're glad to walk your team through isolation, permissions, data handling and messaging compliance, and answer a
              security questionnaire as part of your evaluation.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href="/#contact" data-testid="security-book-demo" className="inline-flex items-center justify-center gap-2 rounded-full bg-redline-500 text-white font-semibold px-7 py-3.5 hover:bg-redline-600 transition-colors">
                Talk to us <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/about" className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 text-white font-semibold px-7 py-3.5 hover:bg-white/15 transition-colors">
                About Ask Redline
              </Link>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
